import React, { useEffect, useState } from 'react';
import { plants, properties } from '../services/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import yardrayLogo from '../assets/yardray-logo.png';
import './Dashboard.css';

function formatAppDate(dateStr) {
  if (!dateStr) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) {
    return `${parts[2].padStart(2,"0")}-${months[parseInt(parts[1],10)-1]}-${parts[0]}`;
  }
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

const API = process.env.REACT_APP_API_URL;

const TASK_TYPE_ORDER = [
  'pruning', 'watering', 'fertilizing', 'mowing', 'frost_protection',
  'lawn_care', 'custom'
];

function getTaskTypeLabel(taskType) {
  const labels = {
    pruning: '✂️ Pruning',
    watering: '💧 Watering',
    fertilizing: '🌿 Fertilizing',
    mowing: '🌱 Mowing',
    frost_protection: '❄️ Frost Protection',
    lawn_care: '🏡 Lawn Care',
    custom: '📋 Custom',
  };
  return labels[taskType] || '📋 ' + taskType;
}

function getWeatherIcon(code) {
  if (code === 0) return '☀️';
  if (code <= 2) return '⛅';
  if (code <= 3) return '☁️';
  if (code <= 49) return '🌫️';
  if (code <= 59) return '🌦️';
  if (code <= 69) return '🌧️';
  if (code <= 79) return '🌨️';
  if (code <= 84) return '🌧️';
  if (code <= 99) return '⛈️';
  return '🌡️';
}

function getWeatherDesc(code) {
  if (code === 0) return 'Clear sky';
  if (code <= 2) return 'Partly cloudy';
  if (code <= 3) return 'Overcast';
  if (code <= 49) return 'Foggy';
  if (code <= 59) return 'Drizzle';
  if (code <= 69) return 'Rain';
  if (code <= 79) return 'Snow';
  if (code <= 84) return 'Rain showers';
  if (code <= 99) return 'Thunderstorm';
  return 'Unknown';
}

function formatDate() {
  const now = new Date();
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const hours = String(now.getHours()).padStart(2, '0');
  const mins = String(now.getMinutes()).padStart(2, '0');
  return `${days[now.getDay()]}, ${String(now.getDate()).padStart(2,'0')}-${months[now.getMonth()]}-${now.getFullYear()}  ${hours}:${mins}`;
}

function isToday(dateStr) {
  const taskDate = dateStr.split('T')[0];
  const todayDate = new Date().toISOString().split('T')[0];
  return taskDate === todayDate;
}

function isOverdue(dateStr) {
  const taskDate = dateStr.split('T')[0];
  const todayDate = new Date().toISOString().split('T')[0];
  return taskDate < todayDate;
}

function getAlertBarColor(alerts) {
  if (!alerts?.length) return null;
  const severities = alerts.map(a => a.severity);
  if (severities.includes('Extreme') || severities.includes('Severe')) return '#b71c1c';
  if (severities.includes('Moderate')) return '#e65100';
  return '#f57f17';
}

function Dashboard() {
  const [userPlants, setUserPlants] = useState([]);
  const [userProperties, setUserProperties] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [needsAttentionTasks, setNeedsAttentionTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [weather, setWeather] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [freezeDismissed, setFreezeDismissed] = useState(false);
  const [currentTime, setCurrentTime] = useState(formatDate());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const units = localStorage.getItem('gardenbuddy_units') || 'F';
  const formatTemp = (c) => {
    if (units === 'C') return `${Math.round(c)}°C`;
    return `${Math.round(c * 9 / 5 + 32)}°F`;
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(formatDate()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoGeneratePruningTasks = async (allProps, headers) => {
    try {
      await Promise.all(
        allProps.map(prop =>
          axios.post(`${API}/tasks/generate-pruning/${prop.id}`, {}, { headers })
            .catch(() => {}) // silent — don't block dashboard load
        )
      );
    } catch (err) {
      // silent
    }
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [plantsRes, propsRes] = await Promise.all([
        plants.getAll(),
        properties.getAll(),
      ]);

      const allPlants = plantsRes.data.plants;
      const allProps = propsRes.data.properties;
      setUserProperties(allProps);

      // Auto-generate pruning tasks for all properties, then reload tasks
      await autoGeneratePruningTasks(allProps, headers);

      const tasksRes = await axios.get(`${API}/tasks`, { headers });
      const allTasksList = tasksRes.data.tasks;
      setAllTasks(allTasksList);

      const today = new Date().toISOString().split('T')[0];
      const currentMonth = new Date().getMonth() + 1;
      const pending = allTasksList.filter(t => t.status === 'pending');

      setTodayTasks(pending.filter(t => isToday(t.scheduled_for)));

      // Needs Attention = overdue tasks + pending pruning tasks this month
      // sorted by task type, deduplicated by id
      const overdue = pending.filter(t => isOverdue(t.scheduled_for));
      const pendingPruningThisMonth = pending.filter(t =>
        t.task_type === 'pruning' &&
        new Date(t.scheduled_for).getMonth() + 1 === currentMonth &&
        !isOverdue(t.scheduled_for) &&
        t.scheduled_for.split('T')[0] !== today
      );

      const combined = [...overdue, ...pendingPruningThisMonth];
      const deduped = combined.filter((t, idx, self) =>
        idx === self.findIndex(x => x.id === t.id)
      );
      const sorted = deduped.sort((a, b) => {
        const aOrder = TASK_TYPE_ORDER.indexOf(a.task_type);
        const bOrder = TASK_TYPE_ORDER.indexOf(b.task_type);
        const aIdx = aOrder === -1 ? 99 : aOrder;
        const bIdx = bOrder === -1 ? 99 : bOrder;
        return aIdx - bIdx;
      });
      setNeedsAttentionTasks(sorted);

      const plantsWithPhotos = await Promise.all(
        allPlants.slice(0, 6).map(async (plant) => {
          try {
            const photosRes = await axios.get(`${API}/photos/plant/${plant.id}`, { headers });
            return {
              ...plant,
              firstPhoto: photosRes.data.photos[0]?.thumbnail_urls?.[0] ||
                          photosRes.data.photos[0]?.original_url
            };
          } catch {
            console.warn(`Dashboard: failed to load photos for plant ${plant.id}`);
            return plant;
          }
        })
      );
      setUserPlants({ all: allPlants, withPhotos: plantsWithPhotos });

      const propWithCoords = allProps.find(p => p.latitude && p.longitude);
      if (propWithCoords) {
        try {
          const [wRes, alertsRes] = await Promise.all([
            axios.get(`${API}/weather/location`, {
              headers,
              params: { latitude: propWithCoords.latitude, longitude: propWithCoords.longitude }
            }),
            axios.get(`${API}/weather/alerts`, {
              headers,
              params: { latitude: propWithCoords.latitude, longitude: propWithCoords.longitude }
            }).catch(() => ({ data: { alerts: [] } }))
          ]);

          const w = wRes.data;
          const dailyMins = w.daily?.temperature_2m_min || [];
          const hasFreeze = dailyMins.slice(0, 3).some(t => t <= 2);
          setWeather({
            temp: w.current?.temperature_2m,
            code: w.current?.weather_code,
            wind: Math.round(w.current?.wind_speed_10m),
            propName: propWithCoords.name,
            hasFreeze
          });
          setAlerts(alertsRes.data.alerts || []);
        } catch (err) {
          console.warn('Dashboard: weather fetch failed:', err.message);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Dashboard: loadData failed:', err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading...</div>;
  }

  const totalPlants = userPlants?.all?.length ?? 0;
  const recentPlants = userPlants?.withPhotos ?? [];
  const visibleAlerts = alerts.filter(a => a.severity !== 'Unknown');
  const alertBarColor = getAlertBarColor(visibleAlerts);
  const topAlert = visibleAlerts[0];
  const totalPending = allTasks.filter(t => t.status === 'pending').length;

  if (totalPlants === 0 && userProperties.length === 0) {
    return (
      <div className="dashboard-welcome">
        <img src={yardrayLogo} alt="Yardray" className="dashboard-welcome-logo" />
        <h2>Welcome to Yardray!</h2>
        <p>Start by creating your first property.</p>
        <button className="dashboard-welcome-btn" onClick={() => navigate('/properties')}>
          Create Property
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">

      {topAlert && !alertDismissed && (
        <div
          className="dashboard-alert-bar"
          style={{ backgroundColor: alertBarColor }}
          onClick={() => navigate('/weather')}
        >
          <div className="dashboard-alert-text">
            {topAlert.severity === 'Extreme' || topAlert.severity === 'Severe' ? '🚨' : '⚠️'} {topAlert.event} · Tap for details →
          </div>
          <button
            className="dashboard-dismiss-btn"
            onClick={(e) => { e.stopPropagation(); setAlertDismissed(true); }}
          >
            ✕
          </button>
        </div>
      )}

      {weather?.hasFreeze && !freezeDismissed && (
        <div className="dashboard-freeze-bar" onClick={() => navigate('/weather')}>
          <div className="dashboard-freeze-text">Freeze Warning · Tap for details →</div>
          <button
            className="dashboard-dismiss-btn"
            onClick={(e) => { e.stopPropagation(); setFreezeDismissed(true); }}
          >
            ✕
          </button>
        </div>
      )}

      <div className="dashboard-time-bar">{currentTime}</div>

      {weather && (
        <div className="dashboard-weather-card" onClick={() => navigate('/weather')}>
          <div className="dashboard-weather-left">
            <span className="dashboard-weather-icon">{getWeatherIcon(weather.code)}</span>
            <div>
              <div className="dashboard-weather-temp">{formatTemp(weather.temp)} — {getWeatherDesc(weather.code)}</div>
              <div className="dashboard-weather-detail">{weather.propName} · Wind {weather.wind} km/h</div>
            </div>
          </div>
          <span className="dashboard-weather-link">Full forecast →</span>
        </div>
      )}

      {needsAttentionTasks.length > 0 && (
        <div className="dashboard-overdue-card" onClick={() => navigate('/tasks')}>
          <div className="dashboard-overdue-title">
            ⚠️ Needs Attention · {needsAttentionTasks.length} task{needsAttentionTasks.length > 1 ? 's' : ''}
          </div>
          {needsAttentionTasks.slice(0, 4).map(t => (
            <div key={t.id} className="dashboard-overdue-item">
              · {getTaskTypeLabel(t.task_type)}
              {t.plant_name && <span className="dashboard-overdue-plant"> — {t.plant_name}</span>}
              {isOverdue(t.scheduled_for) && (
                <span className="dashboard-overdue-date"> {formatAppDate(t.scheduled_for)}</span>
              )}
            </div>
          ))}
          {needsAttentionTasks.length > 4 && (
            <div className="dashboard-overdue-more">+ {needsAttentionTasks.length - 4} more → View all</div>
          )}
        </div>
      )}

      <div className="dashboard-tasks-card">
        <div className="dashboard-tasks-header">
          <h3>
            Today's Tasks
            <span className={`dashboard-tasks-count${todayTasks.length > 0 ? ' dashboard-tasks-count--has-tasks' : ' dashboard-tasks-count--empty'}`}>
              {todayTasks.length}
            </span>
          </h3>
          <span className="dashboard-tasks-link" onClick={() => navigate('/tasks')}>View all →</span>
        </div>
        {todayTasks.length === 0 ? (
          <div className="dashboard-tasks-empty">No tasks scheduled for today.</div>
        ) : (
          <div className="dashboard-tasks-list">
            {todayTasks.slice(0, 5).map(task => (
              <div key={task.id} className="dashboard-task-row" onClick={() => navigate('/tasks')}>
                <div>
                  <span className="dashboard-task-name">{task.title}</span>
                  {task.plant_name && (
                    <span className="dashboard-task-plant">· {task.plant_name}</span>
                  )}
                </div>
                <span className="dashboard-task-today-badge">Today</span>
              </div>
            ))}
            {todayTasks.length > 5 && (
              <div className="dashboard-tasks-more" onClick={() => navigate('/tasks')}>
                + {todayTasks.length - 5} more tasks today →
              </div>
            )}
          </div>
        )}
      </div>

      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-plants" onClick={() => navigate('/plants')}>
          <div className="dashboard-stat-value-plants">{totalPlants}</div>
          <div className="dashboard-stat-label">Plants</div>
        </div>
        <div className="dashboard-stat-tasks" onClick={() => navigate('/tasks')}>
          <div className="dashboard-stat-value-tasks">{totalPending}</div>
          <div className="dashboard-stat-label">Tasks Pending</div>
        </div>
        <div className="dashboard-stat-seeds" onClick={() => navigate('/seed-starting')}>
          <div className="dashboard-stat-icon">🌱</div>
          <div className="dashboard-stat-label">Seeds</div>
        </div>
      </div>

      <div className="dashboard-recent-card">
        <div className="dashboard-recent-header">
          <h3>Recent Plants</h3>
          <span className="dashboard-recent-link" onClick={() => navigate('/plants')}>
            View all {totalPlants} →
          </span>
        </div>
        {recentPlants.length === 0 ? (
          <div className="dashboard-recent-empty">
            <p>No plants yet.</p>
            <button className="dashboard-add-plant-btn" onClick={() => navigate('/plants/add')}>
              + Add Plant
            </button>
          </div>
        ) : (
          <div className="dashboard-recent-grid">
            {recentPlants.map(plant => (
              <div key={plant.id} className="dashboard-plant-card" onClick={() => navigate(`/plants/${plant.id}`)}>
                <div className="dashboard-plant-thumb">
                  {plant.firstPhoto ? (
                    <img src={plant.firstPhoto} alt={plant.common_name} className="dashboard-plant-thumb-img" />
                  ) : '🌱'}
                </div>
                <div className="dashboard-plant-info">
                  <div className="dashboard-plant-name">{plant.common_name || 'Unknown'}</div>
                  {plant.plant_type && (
                    <div className="dashboard-plant-type">{plant.plant_type}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

export default Dashboard;
